import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { dateShort, money } from "@/lib/format";
import type { Enums } from "@/types";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export type OrderStatus = Enums<"order_status">;
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = { pending: "En attente de validation", confirmed: "Confirmée", in_delivery: "En livraison", delivered: "Livrée", closed: "Clôturée", cancelled: "Annulée" };
export const ORDER_STATUS_VARIANT: Record<OrderStatus, "default" | "secondary" | "destructive" | "outline"> = { pending: "outline", confirmed: "default", in_delivery: "secondary", delivered: "secondary", closed: "outline", cancelled: "destructive" };

export function useOrders(opts: { status?: string; clientId?: string } = {}) {
  return useQuery({
    queryKey: ["orders", opts],
    queryFn: async () => {
      let q = supabase.from("orders").select("*, clients(id, legal_name)").order("created_at", { ascending: false });
      if (opts.status && opts.status !== "all") q = q.eq("status", opts.status as OrderStatus);
      if (opts.clientId) q = q.eq("client_id", opts.clientId);
      const { data, error } = await q; if (error) throw error; return data;
    },
  });
}

export default function OrdersPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const { data: orders, isLoading, error } = useOrders({ status });
  return (
    <div>
      <PageHeader title="Commandes" description="Une commande naît d'un devis accepté. Elle est confirmée quand le contrat est signé et l'acompte encaissé." />
      <div className="mb-4 flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{(Object.keys(ORDER_STATUS_LABEL) as OrderStatus[]).map((k) => <SelectItem key={k} value={k}>{ORDER_STATUS_LABEL[k]}</SelectItem>)}</SelectContent>
        </Select>
        {orders && <span className="ml-auto text-sm text-muted-foreground">{orders.length} commande{orders.length > 1 ? "s" : ""}</span>}
      </div>
      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !orders?.length ? (
        <EmptyState title="Aucune commande" description="Les commandes se créent à partir des devis acceptés." />
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Objet</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Acompte</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} className="cursor-pointer" onClick={() => nav(`/commandes/${o.id}`)}>
                  <TableCell className="font-mono text-xs">{o.number}</TableCell>
                  <TableCell className="font-medium">{o.clients?.legal_name}</TableCell>
                  <TableCell className="text-muted-foreground">{o.title ?? "—"}</TableCell>
                  <TableCell>{dateShort(o.ordered_at)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(o.total, o.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(o.deposit_amount, o.currency)}</TableCell>
                  <TableCell><Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
